<script>
// TeamForm.vue
import validations from './helpers/validations';
import FormInput from 'v3/components/Form/Input.vue';
import { reactive, computed, watch, ref } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { useMapGetter } from 'dashboard/composables/store';
import { minValue } from '@vuelidate/validators';

import NextButton from 'dashboard/components-next/button/Button.vue';

// Padrão do banco (teams.max_redistribution_conversations): com 0 a fila
// parava quando ninguém estava online na chegada (F3).
const DEFAULT_REDISTRIBUTION = 5;

export default {
  components: {
    NextButton,
    FormInput,
  },
  props: {
    onSubmit: {
      type: Function,
      default: () => {},
    },
    submitInProgress: {
      type: Boolean,
      default: false,
    },
    formData: {
      type: Object,
      default: () => {},
    },
    submitButtonText: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const formData = props.formData || {};
    const {
      description = '',
      name: title = '',
      allow_auto_assign: allowAutoAssign = false,
      label_ids: labelIds = [],
      max_assignment_limit: maxAssignmentLimit = null,
      max_redistribution_conversations:
        maxRedistributionConversations = DEFAULT_REDISTRIBUTION,
    } = formData;

    const state = reactive({
      description,
      title,
      allowAutoAssign,
      selectedLabels: labelIds || [],
      maxAssignmentLimit,
      maxRedistributionConversations,
    });

    const labels = useMapGetter('labels/getLabels');

    const availableLabels = computed(() => {
      return labels.value || [];
    });

    // O lote acompanha o limite de atribuição enquanto ninguém o editou nesta
    // tela (mesma regra do Team#fit_redistribution_to_assignment_limit): time
    // novo fica com min(padrão, limite) — antes ligar a atribuição punha limite
    // 1, o lote caía pra 1 e ficava em 1 depois de subir o limite —; time
    // existente só desce quando o limite fica abaixo do lote.
    const isNewTeam = !formData.id;
    const redistributionChosen = ref(false);
    watch(
      () => state.maxAssignmentLimit,
      limit => {
        if (!limit || redistributionChosen.value) return;
        if (isNewTeam) {
          state.maxRedistributionConversations = Math.min(
            DEFAULT_REDISTRIBUTION,
            limit
          );
        } else if (state.maxRedistributionConversations > limit) {
          state.maxRedistributionConversations = limit;
        }
      }
    );

    // Watch para setar valores mínimos quando habilitar auto assign
    watch(
      () => state.allowAutoAssign,
      newValue => {
        if (newValue && !state.maxAssignmentLimit) {
          state.maxAssignmentLimit = 1;
        }
      }
    );

    // Validações combinadas
    const combinedRules = {
      ...validations,
      maxAssignmentLimit: {
        minValue: minValue(1),
      },
      maxRedistributionConversations: {
        minValue: minValue(0),
      },
    };

    const v$ = useVuelidate(combinedRules, state);

    return {
      state,
      v$,
      availableLabels,
      redistributionChosen,
    };
  },

  computed: {
    maxAssignmentLimitErrors() {
      if (this.v$.maxAssignmentLimit.$error) {
        return (
          this.$t(
            'INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT_RANGE_ERROR'
          ) || 'O limite deve ser no mínimo 1'
        );
      }
      return '';
    },
    maxRedistributionConversationsErrors() {
      if (this.v$.maxRedistributionConversations.$error) {
        return (
          this.$t(
            'TEAMS_SETTINGS.FORM.MAX_REDISTRIBUTION_CONVERSATIONS.ERROR'
          ) || 'O valor deve ser no mínimo 0'
        );
      }

      // Validar se não excede o max_assignment_limit
      if (
        this.state.maxAssignmentLimit &&
        this.state.maxRedistributionConversations >
          this.state.maxAssignmentLimit
      ) {
        return (
          this.$t(
            'TEAMS_SETTINGS.FORM.MAX_REDISTRIBUTION_CONVERSATIONS.EXCEEDS_LIMIT'
          ) ||
          `Não pode exceder o limite de atribuição (${this.state.maxAssignmentLimit})`
        );
      }

      return '';
    },
    hasRedistributionError() {
      return this.maxRedistributionConversationsErrors !== '';
    },
    isSubmitDisabled() {
      return (
        this.v$.title.$invalid ||
        this.submitInProgress ||
        this.hasRedistributionError
      );
    },
  },

  methods: {
    handleSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid || this.hasRedistributionError) {
        return;
      }

      const payload = {
        description: this.state.description,
        name: this.state.title,
        allow_auto_assign: this.state.allowAutoAssign,
        label_ids: [...this.state.selectedLabels],
      };

      // Adiciona max_assignment_limit apenas se auto_assign estiver habilitado
      if (this.state.allowAutoAssign && this.state.maxAssignmentLimit) {
        payload.max_assignment_limit = this.state.maxAssignmentLimit;
      }

      // Adiciona max_redistribution_conversations apenas se auto_assign estiver habilitado
      if (this.state.allowAutoAssign) {
        payload.max_redistribution_conversations =
          this.state.maxRedistributionConversations || 0;
      }

      this.onSubmit(payload);
    },

    toggleLabel(labelId) {
      const index = this.state.selectedLabels.indexOf(labelId);
      if (index > -1) {
        this.state.selectedLabels.splice(index, 1);
      } else {
        this.state.selectedLabels.push(labelId);
      }
    },

    isLabelSelected(labelId) {
      return this.state.selectedLabels.includes(labelId);
    },
  },
};
</script>

<template>
  <div class="flex-shrink-0 w-full">
    <form class="mx-0 grid gap-4" @submit.prevent="handleSubmit">
      <FormInput
        v-model="state.title"
        name="title"
        spacing="compact"
        :label="$t('TEAMS_SETTINGS.FORM.NAME.LABEL')"
        :placeholder="$t('TEAMS_SETTINGS.FORM.NAME.PLACEHOLDER')"
        :has-error="v$.title.$error"
        :error-message="v$.title.$error ? v$.title.$errors[0].$message : ''"
        @blur="v$.title.$touch"
      />

      <FormInput
        v-model="state.description"
        name="description"
        spacing="compact"
        :label="$t('TEAMS_SETTINGS.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('TEAMS_SETTINGS.FORM.DESCRIPTION.PLACEHOLDER')"
        :has-error="v$.description.$error"
        :error-message="
          v$.description.$error ? v$.description.$errors[0].$message : ''
        "
        @blur="v$.description.$touch"
      />

      <!-- Multiseleção de Labels -->
      <div class="w-full">
        <label class="block text-sm font-medium mb-2">
          {{ $t('TEAMS_SETTINGS.FORM.LABELS.LABEL') || 'Labels' }}
        </label>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-3 w-full">
          <div
            v-if="availableLabels.length === 0"
            class="text-n-slate-9 text-sm"
          >
            {{
              $t('TEAMS_SETTINGS.FORM.LABELS.NO_LABELS') ||
              'Nenhum label disponível'
            }}
          </div>
          <div class="flex flex-wrap gap-2">
            <label
              v-for="label in availableLabels"
              :key="label.id"
              class="inline-flex items-center gap-2 px-3 py-2 border border-n-weak rounded-lg cursor-pointer hover:bg-n-solid-2 transition-colors"
              :class="
                isLabelSelected(label.id) ? 'bg-n-iris-2 border-n-iris-6' : ''
              "
            >
              <input
                :checked="isLabelSelected(label.id)"
                type="checkbox"
                :value="label.id"
                class="cursor-pointer"
                @change="toggleLabel(label.id)"
              />
              <span
                v-if="label.color"
                class="size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm flex-shrink-0"
                :style="{ backgroundColor: label.color }"
              />
              <span class="text-sm text-n-slate-12">{{ label.title }}</span>
            </label>
          </div>
        </div>
        <div
          v-if="state.selectedLabels.length > 0"
          class="mt-2 text-xs text-n-slate-9"
        >
          {{ state.selectedLabels.length }}
          {{
            state.selectedLabels.length === 1
              ? 'etiqueta selecionada'
              : 'etiquetas selecionadas'
          }}
        </div>
      </div>

      <!-- Atribuição Automática de Conversas -->
      <div class="w-full space-y-3">
        <div class="border-t border-n-weak pt-3">
          <h3 class="text-sm font-medium mb-3">
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.AGENT_ASSIGNMENT') ||
              'Atribuição de Agentes'
            }}
          </h3>

          <div class="flex items-start gap-2">
            <input
              id="allowAutoAssign"
              v-model="state.allowAutoAssign"
              type="checkbox"
              class="mt-2"
            />
            <div class="flex-1">
              <label for="allowAutoAssign" class="cursor-pointer">
                {{
                  $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_ASSIGNMENT') ||
                  'Habilitar atribuição automática'
                }}
              </label>
              <p class="text-xs text-n-slate-11 mt-1">
                {{
                  $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_ASSIGNMENT_SUB_TEXT') ||
                  'As conversas serão atribuídas automaticamente aos agentes quando chegarem.'
                }}
              </p>
            </div>
          </div>

          <!-- Campo de Limite Máximo de Atribuições -->
          <div v-if="state.allowAutoAssign" class="mt-4">
            <FormInput
              v-model.number="state.maxAssignmentLimit"
              name="maxAssignmentLimit"
              type="number"
              spacing="compact"
              min="1"
              :label="
                $t('INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT') ||
                'Limite máximo de conversas por agente'
              "
              placeholder=""
              :has-error="v$.maxAssignmentLimit.$error"
              :error-message="maxAssignmentLimitErrors"
              @blur="v$.maxAssignmentLimit.$touch"
            />
            <p class="text-xs text-n-slate-11 mt-1">
              {{
                $t(
                  'INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT_SUB_TEXT'
                ) ||
                'Número máximo de conversas abertas que podem ser atribuídas a um agente.'
              }}
            </p>
          </div>

          <!-- Campo de Máximo de Conversas por Redistribuição -->
          <div v-if="state.allowAutoAssign" class="mt-4">
            <FormInput
              v-model.number="state.maxRedistributionConversations"
              name="maxRedistributionConversations"
              type="number"
              spacing="compact"
              min="0"
              :label="
                $t(
                  'TEAMS_SETTINGS.FORM.MAX_REDISTRIBUTION_CONVERSATIONS.LABEL'
                ) || 'Máximo de conversas por redistribuição'
              "
              placeholder="0"
              :has-error="hasRedistributionError"
              :error-message="maxRedistributionConversationsErrors"
              @update:model-value="redistributionChosen = true"
              @blur="v$.maxRedistributionConversations.$touch"
            />
            <p class="text-xs text-n-slate-11 mt-1">
              {{
                $t(
                  'TEAMS_SETTINGS.FORM.MAX_REDISTRIBUTION_CONVERSATIONS.SUB_TEXT'
                ) ||
                'Número máximo de conversas não atribuídas que podem ser redistribuídas quando um agente libera capacidade. Use 0 para desabilitar a redistribuição automática.'
              }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
        <div class="w-full">
          <NextButton
            type="submit"
            :label="submitButtonText"
            :disabled="isSubmitDisabled"
            :is-loading="submitInProgress"
          />
        </div>
      </div>
    </form>
  </div>
</template>
